import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/lib/api'
import { authApi } from '@/lib/api/auth'
import {
  whoami,
  SessionUnavailable,
  takeLoginError,
  loginErrorMessage,
  redirectToLogin,
  endSession,
  loginLoopTripped,
} from '@/lib/api/session'
import type { AuthToken, CreateTokenRequest } from '@/types/models'

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const isLoading = ref(true)
  const bearerToken = ref<string | null>(null)
  const user = ref<{ email?: string; username?: string; role?: string; groups?: string[] } | null>(null)
  const tokens = ref<AuthToken[]>([])
  /**
   * Human-readable sign-in problem to show instead of bouncing into login:
   * a `?login_error=<code>` from the API's callback, a 503 from the session
   * probe (login is down), or a tripped loop guard.
   */
  const loginError = ref<string | null>(null)
  /** The raw code behind `loginError`, for tests and telemetry. */
  const loginErrorCode = ref<string | null>(null)

  // Computed
  const hasValidToken = computed(() => !!bearerToken.value)
  const currentUser = computed(() => user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  function setLoginError(code: string | null) {
    loginErrorCode.value = code
    loginError.value = code ? loginErrorMessage(code) : null
  }

  // Actions
  async function initialize() {
    isLoading.value = true
    try {
      // A failed OIDC callback lands on the app URL with ?login_error=<code>.
      // Read it once (this strips it from the URL) so a session-less start
      // shows the message instead of bouncing straight back into the login
      // that just failed.
      const errorCode = takeLoginError()

      // Check for a persisted Bearer token (CLI / programmatic access).
      const token = localStorage.getItem('api_token') || import.meta.env.VITE_API_TOKEN
      if (token) {
        bearerToken.value = token
        apiClient.setBearerToken(token)
        isAuthenticated.value = true
        // With a Bearer token, /api/v1/me is the source of truth.
        await fetchMe()
        return
      }

      // Otherwise use OIDC session cookie via /oidc/userinfo.
      await checkOidcSession()

      if (!isAuthenticated.value) {
        if (errorCode) {
          setLoginError(errorCode)
        } else if (!loginErrorCode.value && loginLoopTripped()) {
          setLoginError('login_loop')
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Probe `GET /oidc/userinfo` to see whether the browser has a valid session
   * cookie. This endpoint is public and never triggers a redirect. Accepts
   * both today's `200 {authenticated:false}` and the contract's `401` for
   * "no session"; a 503 means login itself is unavailable and is surfaced as
   * a message rather than a redirect.
   */
  async function checkOidcSession() {
    try {
      const session = await whoami()
      if (!session) {
        isAuthenticated.value = false
        user.value = null
        return
      }

      isAuthenticated.value = true
      setLoginError(null)
      user.value = {
        username: session.username ?? session.display_name,
        email: session.email,
        groups: session.groups,
      }

      // Hit /api/v1/me so the server auto-provisions the user record and
      // returns the authoritative role. Failures are non-fatal — the UI will
      // just fall back to whatever userinfo returned.
      try {
        await fetchMe()
      } catch (err) {
        console.warn('Failed to fetch /api/v1/me after OIDC session check:', err)
      }
    } catch (err) {
      isAuthenticated.value = false
      user.value = null
      if (err instanceof SessionUnavailable) {
        setLoginError(err.code)
        return
      }
      console.error('OIDC session check failed:', err)
      setLoginError('internal_error')
    }
  }

  async function fetchMe() {
    const response = await apiClient.get<{
      id: string
      username: string
      email: string
      role: string
    }>('/api/v1/me')

    user.value = {
      username: response.username,
      email: response.email,
      role: response.role,
      groups: user.value?.groups,
    }
    isAuthenticated.value = true
  }

  function setToken(token: string) {
    bearerToken.value = token
    apiClient.setBearerToken(token)
    isAuthenticated.value = true
  }

  function clearAuth() {
    bearerToken.value = null
    user.value = null
    isAuthenticated.value = false
    tokens.value = []
    apiClient.clearBearerToken()
  }

  /**
   * Navigate the browser to the OIDC login endpoint with `return_to` set to a
   * same-origin path (defaults to the current one). `force` bypasses the
   * per-tab loop guard — use it for an explicit sign-in button. Returns false
   * when the guard refused an automatic bounce.
   */
  function login(returnTo?: string, force = false): boolean {
    setLoginError(null)
    const ok = redirectToLogin(returnTo, force)
    if (!ok) setLoginError('login_loop')
    return ok
  }

  /**
   * Clear local state and end the session: `POST /oidc/logout` then navigate
   * to the returned `end_session_url` (tears down the Authelia SSO session
   * too); falls back to `GET /oidc/logout` against today's API.
   */
  async function logout(): Promise<string> {
    clearAuth()
    return endSession()
  }

  async function fetchTokens() {
    try {
      const response = await authApi.listTokens()
      tokens.value = response.data
    } catch (error) {
      console.error('Failed to fetch tokens:', error)
      throw error
    }
  }

  async function createToken(data: CreateTokenRequest): Promise<string> {
    try {
      const response = await authApi.createToken(data)
      tokens.value.unshift({
        id: response.data.id,
        name: response.data.name,
        prefix: response.data.prefix,
        expiresAt: response.data.expiresAt,
        lastUsedAt: null,
        createdAt: response.data.createdAt,
      })
      return response.data.token
    } catch (error) {
      console.error('Failed to create token:', error)
      throw error
    }
  }

  async function revokeToken(id: string) {
    try {
      await authApi.revokeToken(id)
      tokens.value = tokens.value.filter(t => t.id !== id)
    } catch (error) {
      console.error('Failed to revoke token:', error)
      throw error
    }
  }

  return {
    // State
    isAuthenticated,
    isLoading,
    bearerToken,
    user,
    tokens,
    loginError,
    loginErrorCode,
    // Computed
    hasValidToken,
    currentUser,
    isAdmin,
    // Actions
    initialize,
    checkOidcSession,
    setToken,
    clearAuth,
    login,
    logout,
    fetchTokens,
    createToken,
    revokeToken,
  }
})
